import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Box, Typography } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useDispatch } from "react-redux";
import CommonDrawerLayout from "../../../common_components/CommonDrawerLayout";
import AppButton from "../../../common_components/AppButton";
import { TextInput, NumberInput, TextArea, Dropdown, FormRow, FormField } from "../../../common_components/FormInputs";
import SingleDatePicker from "../../../common_components/daterangepicker/SingleDatePicker";
import { toast } from "../../../common_components/Toast/ToastUtils";
import { fetchOutreachClientMeta, fetchOutreachCounts, manageOutreachTracker, } from "../../../redux/outreachTrackerSlice";
import { STATUS_OPTIONS, DRAWER_TYPE_OPTIONS, } from "../utils";
import { toCalendarDate, fromCalendarDate, NA_TEXT } from "../Component/shared";
const EMPTY_FORM = {
    client_id: "",
    am_id: [],
    os_id: "",
    type: "",
    tracker_ref_id: "",
    launch_date: "",
    end_date: "",
    status: "pending_keywords",
    keywords: "",
    pitch: "",
    tracker: "",
    contact_info: { total: 0, enrolled: 0, unenrolled: 0, bounced: 0, sent: 0 },
    lead_info: { total: 0, positive: 0, negative: 0, neutral: 0 },
    post_mortem: "",
    notes: "",
};
const OutreachTrackerDrawer = ({ open, onClose, mode, record, filters, onSaved }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [amOptions, setAmOptions] = useState([]);
    const [trackerOptions, setTrackerOptions] = useState([]);
    // True only when the user actively changes the client — so we auto-assign the
    // client's AM on a user action, but never clobber an existing record's stored AM
    // when the drawer first opens in edit mode.
    const userChangedClientRef = useRef(false);
    const clientOptions = useMemo(() => filters.clients.map((c) => ({ value: c.id, label: c.name })), [filters.clients]);
    const osOptions = useMemo(() => filters.os_users.map((u) => ({ value: u.id, label: u.name })), [filters.os_users]);
    // The account managers are the client's affiliate leads — all of them are auto-selected
    // and shown read-only (not user-editable). Build a name lookup that also covers a record's
    // historical AMs (which may no longer be the client's handlers) so edit mode shows names.
    const amNameById = useMemo(() => {
        const map = new Map();
        amOptions.forEach((a) => map.set(a.id, a.name));
        (record?.am || []).forEach((a) => { if (a?.id && !map.has(a.id))
            map.set(a.id, a.name); });
        return map;
    }, [amOptions, record]);
    const selectedAmName = useMemo(() => formData.am_id.map((id) => amNameById.get(id) || "").filter(Boolean).join(", ") || NA_TEXT, [amNameById, formData.am_id]);
    const trackerDropdownOptions = useMemo(() => trackerOptions.map((t) => ({ value: t.id, label: t.name })), [trackerOptions]);
    // Totals are always the sum of their parts — derived (read-only), not editable.
    const { enrolled, unenrolled, bounced, sent } = formData.contact_info;
    const contactTotal = useMemo(() => enrolled + unenrolled + bounced + sent, [enrolled, unenrolled, bounced, sent]);
    const { positive, negative, neutral } = formData.lead_info;
    const leadTotal = useMemo(() => positive + negative + neutral, [positive, negative, neutral]);
    useEffect(() => {
        if (!open)
            return;
        if (mode === "edit" && record) {
            setFormData({
                id: record.id,
                client_id: record.client?.id || "",
                am_id: (record.am || []).map((a) => a.id),
                os_id: record.os?.id || "",
                type: record.type || "",
                tracker_ref_id: record.tracker_ref_id || "",
                launch_date: record.launch_date || "",
                end_date: record.end_date || "",
                status: record.status || "pending_keywords",
                keywords: record.keywords || "",
                pitch: record.pitch || "",
                tracker: record.tracker || "",
                contact_info: { ...EMPTY_FORM.contact_info, ...record.contact_info },
                lead_info: { ...EMPTY_FORM.lead_info, ...record.lead_info },
                post_mortem: record.post_mortem || "",
                notes: record.notes || "",
            });
        }
        else {
            setFormData(EMPTY_FORM);
            setAmOptions([]);
            setTrackerOptions([]);
        }
        setErrors({});
    }, [open, mode, record]);
    useEffect(() => {
        if (!open || !formData.client_id)
            return;
        let cancelled = false;
        (async () => {
            try {
                const meta = await dispatch(fetchOutreachClientMeta({ client_id: formData.client_id, type: formData.type })).unwrap();
                if (cancelled)
                    return;
                setAmOptions(meta.account_managers);
                setTrackerOptions(meta.trackers);
                // Only auto-fill the AM when the user picked the client. On initial
                // edit load, keep the record's stored AM (matches the list view).
                if (userChangedClientRef.current) {
                    userChangedClientRef.current = false;
                    // Auto-select ALL of the client's account managers (affiliate leads).
                    setFormData((prev) => ({ ...prev, am_id: meta.account_managers.map((a) => a.id) }));
                }
            }
            catch {
                if (!cancelled) {
                    setAmOptions([]);
                    setTrackerOptions([]);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, formData.client_id, formData.type, dispatch]);
    const clearError = (field) => setErrors((prev) => {
        if (!prev[field])
            return prev;
        const next = { ...prev };
        delete next[field];
        return next;
    });
    const setField = useCallback((field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    }, []);
    const handleClientChange = (val) => {
        userChangedClientRef.current = true;
        setFormData((prev) => ({ ...prev, client_id: val, am_id: [], tracker_ref_id: "" }));
        clearError("client_id");
    };
    const handleTypeChange = (val) => {
        const type = val;
        setFormData((prev) => ({
            ...prev,
            type,
            tracker_ref_id: "",
        }));
        clearError("type");
    };
    const handleTrackerChange = async (val) => {
        const trackerId = val;
        setFormData((prev) => ({ ...prev, tracker_ref_id: trackerId }));
        if (trackerId && formData.type) {
            try {
                const counts = await dispatch(fetchOutreachCounts({ type: formData.type, tracker_ref_id: trackerId })).unwrap();
                setFormData((prev) => ({ ...prev, contact_info: counts.contact_info, lead_info: counts.lead_info }));
            }
            catch {
            }
        }
    };
    const setCount = (group, key) => (e) => {
        const num = Number(e.target.value);
        setFormData((prev) => ({
            ...prev,
            [group]: { ...prev[group], [key]: isNaN(num) ? 0 : num },
        }));
    };
    // All fields are optional — the only requirement is that the user entered at least
    // one field. `status` defaults to 'pending_keywords' on load, so it counts only when
    // changed; counts count only when some value is non-zero.
    const hasAnyData = () => {
        const f = formData;
        const countHasData = (g) => Object.values(g).some((v) => Number(v) !== 0);
        return (!!f.client_id || f.am_id.length > 0 || !!f.os_id || !!f.type || !!f.tracker_ref_id ||
            !!f.launch_date || !!f.end_date ||
            (!!f.status && f.status !== "pending_keywords") ||
            !!f.keywords.trim() || !!f.pitch.trim() || !!f.tracker.trim() ||
            !!f.post_mortem.trim() || !!f.notes.trim() ||
            countHasData(f.contact_info) || countHasData(f.lead_info));
    };
    const validate = () => {
        const next = {};
        if (!hasAnyData()) {
            toast.error("Please fill in at least one field before saving.");
            setErrors(next);
            return false;
        }
        if (formData.launch_date && formData.end_date && new Date(formData.end_date) < new Date(formData.launch_date)) {
            next.end_date = "End date cannot be before the launch date.";
            toast.error("End date cannot be before the launch date.");
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };
    const handleSave = async () => {
        if (!validate())
            return;
        setSaving(true);
        try {
            // Persist totals as the sum of their parts, matching what the form shows.
            const payload = {
                ...formData,
                contact_info: { ...formData.contact_info, total: contactTotal },
                lead_info: { ...formData.lead_info, total: leadTotal },
            };
            await dispatch(manageOutreachTracker({ action: mode === "edit" ? "update" : "create", ...payload })).unwrap();
            toast.success(mode === "edit" ? "Outreach tracker updated." : "Outreach tracker created.");
            onSaved();
            onClose();
        }
        catch (error) {
            toast.error(typeof error === "string" ? error : error?.message || "Failed to save outreach tracker.");
        }
        finally {
            setSaving(false);
        }
    };
    const headerActions = (_jsx(AppButton, { variant: "contained", color: "primary", size: "small", startIcon: _jsx(SaveIcon, { sx: { fontSize: "16px !important" } }), onClick: handleSave, disabled: saving, className: "outreach-tracker-save-button", children: saving ? "Saving..." : "Save" }));
    return (_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: _jsx(Typography, { className: "outreach-tracker-drawer-title", children: mode === "edit" ? "Edit Outreach Tracker" : "Add Outreach Tracker" }), headerActions: headerActions, width: "md", className: "outreach-tracker-drawer", children: _jsxs(Box, { className: "outreach-tracker-drawer-wrapper", children: [_jsxs(Box, { className: "outreach-tracker-section", children: [_jsx(Typography, { className: "outreach-tracker-section-title", sx: { fontWeight: 600 }, children: "Basic Information" }), _jsxs(FormRow, { columns: 2, gap: 16, children: [_jsx(FormField, { children: _jsx(Dropdown, { label: "Client", value: formData.client_id, onChange: handleClientChange, options: clientOptions, placeholder: "Select Client", searchable: true, error: !!errors.client_id, errorMessage: errors.client_id }) }), _jsx(FormField, { children: _jsx(TextInput, { label: "Account Manager", value: selectedAmName, disabled: true, className: selectedAmName === NA_TEXT ? "outreach-tracker-na-value" : "" }) })] }), _jsx(FormRow, { columns: 1, gap: 16, children: _jsx(FormField, { children: _jsx(Dropdown, { label: "OS", value: formData.os_id, onChange: (val) => setField("os_id", val), options: osOptions, placeholder: "Select OS", searchable: true }) }) })] }), _jsxs(Box, { className: "outreach-tracker-section", children: [_jsx(Typography, { className: "outreach-tracker-section-title", sx: { fontWeight: 600 }, children: "Tracker Status" }), _jsxs(FormRow, { columns: 2, gap: 16, children: [_jsx(FormField, { children: _jsx(Dropdown, { label: "Type", value: formData.type, onChange: handleTypeChange, options: DRAWER_TYPE_OPTIONS, placeholder: "Select Type", error: !!errors.type, errorMessage: errors.type }) }), _jsx(FormField, { children: _jsx(Dropdown, { label: "Tracker", value: formData.tracker_ref_id, onChange: handleTrackerChange, options: trackerDropdownOptions, placeholder: "Select Tracker", searchable: true, disabled: !formData.type }) })] }), _jsxs(FormRow, { columns: 3, gap: 16, children: [_jsx(FormField, { children: _jsx(SingleDatePicker, { label: "Launch Date", value: toCalendarDate(formData.launch_date), onChange: (d) => setField("launch_date", fromCalendarDate(d)), allowToday: true, placeholder: "Select Date" }) }), _jsx(FormField, { children: _jsx(SingleDatePicker, { label: "End Date", value: toCalendarDate(formData.end_date), onChange: (d) => setField("end_date", fromCalendarDate(d)), allowToday: true, placeholder: "Select Date" }) }), _jsx(FormField, { children: _jsx(Dropdown, { label: "Status", value: formData.status, onChange: (val) => setField("status", val), options: STATUS_OPTIONS, placeholder: "Select Status", searchable: true }) })] })] }), _jsxs(Box, { className: "outreach-tracker-section", children: [_jsx(Typography, { className: "outreach-tracker-section-title", sx: { fontWeight: 600 }, children: "Tracker Info" }), _jsx(FormRow, { columns: 1, gap: 16, children: _jsx(FormField, { children: _jsx(TextInput, { label: "Keywords", value: formData.keywords, onChange: (e) => setField("keywords", e.target.value), placeholder: "Keywords", leftIcon: "/assets/link.svg" }) }) }), _jsx(FormRow, { columns: 1, gap: 16, children: _jsx(FormField, { children: _jsx(TextInput, { label: "Pitch", value: formData.pitch, onChange: (e) => setField("pitch", e.target.value), placeholder: "Pitch", leftIcon: "/assets/link.svg" }) }) }), _jsx(FormRow, { columns: 1, gap: 16, children: _jsx(FormField, { children: _jsx(TextInput, { label: "Tracker", value: formData.tracker, onChange: (e) => setField("tracker", e.target.value), placeholder: "Tracker", leftIcon: "/assets/link.svg" }) }) })] }), _jsxs(Box, { className: "outreach-tracker-section", children: [_jsx(Typography, { className: "outreach-tracker-section-title", sx: { fontWeight: 600 }, children: "Contact Info" }), _jsxs(FormRow, { columns: 3, gap: 16, children: [_jsx(FormField, { children: _jsx(NumberInput, { label: "Total", value: contactTotal, onChange: () => { }, allowDecimal: false, disabled: true }) }), _jsx(FormField, { children: _jsx(NumberInput, { label: "Enrolled", value: formData.contact_info.enrolled, onChange: setCount("contact_info", "enrolled"), allowDecimal: false }) }), _jsx(FormField, { children: _jsx(NumberInput, { label: "Unenrolled", value: formData.contact_info.unenrolled, onChange: setCount("contact_info", "unenrolled"), allowDecimal: false }) })] }), _jsxs(FormRow, { columns: 2, gap: 16, children: [_jsx(FormField, { children: _jsx(NumberInput, { label: "Bounced", value: formData.contact_info.bounced, onChange: setCount("contact_info", "bounced"), allowDecimal: false }) }), _jsx(FormField, { children: _jsx(NumberInput, { label: "Sent", value: formData.contact_info.sent, onChange: setCount("contact_info", "sent"), allowDecimal: false }) })] })] }), _jsxs(Box, { className: "outreach-tracker-section", children: [_jsx(Typography, { className: "outreach-tracker-section-title", sx: { fontWeight: 600 }, children: "Lead Info" }), _jsxs(FormRow, { columns: 4, gap: 16, children: [_jsx(FormField, { children: _jsx(NumberInput, { label: "Total", value: leadTotal, onChange: () => { }, allowDecimal: false, disabled: true }) }), _jsx(FormField, { children: _jsx(NumberInput, { label: "Positive", value: formData.lead_info.positive, onChange: setCount("lead_info", "positive"), allowDecimal: false }) }), _jsx(FormField, { children: _jsx(NumberInput, { label: "Negative", value: formData.lead_info.negative, onChange: setCount("lead_info", "negative"), allowDecimal: false }) }), _jsx(FormField, { children: _jsx(NumberInput, { label: "Neutral", value: formData.lead_info.neutral, onChange: setCount("lead_info", "neutral"), allowDecimal: false }) })] }), _jsx(FormRow, { columns: 1, gap: 16, children: _jsx(FormField, { children: _jsx(TextInput, { label: "Post Mortem", value: formData.post_mortem, onChange: (e) => setField("post_mortem", e.target.value), placeholder: "Enter Post Mortem", leftIcon: "/assets/link.svg" }) }) })] }), _jsxs(Box, { className: "outreach-tracker-section", children: [_jsx(Typography, { className: "outreach-tracker-section-title", sx: { fontWeight: 600 }, children: "Notes" }), _jsx(FormRow, { columns: 1, gap: 16, children: _jsx(FormField, { children: _jsx(TextArea, { label: "", value: formData.notes, onChange: (e) => setField("notes", e.target.value), placeholder: "Enter Notes here", minRows: 4 }) }) })] })] }) }));
};
export default OutreachTrackerDrawer;
